import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import axiosInstance from '../config/axios';

// Whether the Stamp Inventory feature is switched on (backend Global Option 4003,
// STAMP_ITEMS_ENABLED). This is a database-wide flag, not a per-user permission,
// so it does not ride along on the login payload like showTradeShow / priceBook —
// we read it from GET /stampentry/status.
//
// Used to hide the "Stamp Entry" sidebar item (and could gate anything else) when
// stamping is off, for every role including admin. The value is fetched once per
// session (module-level cache) and mirrored to localStorage so a full reload
// shows the correct state immediately instead of flashing the nav item.

const LS_KEY = 'stampEnabled';

// Session cache + a single in-flight request shared across every hook consumer,
// so the sidebar remounting on each route change never refetches.
let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;

const readCache = (): boolean | null => {
  if (cached !== null) return cached;
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
  return v === null ? null : v === '1';
};

const fetchStatus = (): Promise<boolean> => {
  if (cached !== null) return Promise.resolve(cached);
  if (!inflight) {
    inflight = axiosInstance
      .get('/stampentry/status')
      .then((r) => !!r.data?.enabled)
      // Fail open: if the check errors (network, 403, etc.) don't hide the feature.
      .catch(() => true)
      .then((v) => {
        cached = v;
        try { localStorage.setItem(LS_KEY, v ? '1' : '0'); } catch { /* ignore */ }
        return v;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
};

export const useStampEnabled = (): boolean => {
  const { role } = useSelector((state: RootState) => state.auth);
  // Only the roles whose sidebar can contain "Stamp Entry" need the check; the
  // status endpoint is behind MANAGER/SALES auth and other roles have no stamp nav.
  const gated = role === 'distributor' || role === 'sales';

  // Optimistic default = show. Stamping is on in the common case, so defaulting to
  // visible avoids a flash-in for most users; localStorage makes later loads exact.
  const [enabled, setEnabled] = useState<boolean>(() => readCache() ?? true);

  useEffect(() => {
    if (!gated) return;
    let alive = true;
    fetchStatus().then((v) => { if (alive) setEnabled(v); });
    return () => { alive = false; };
  }, [gated]);

  return gated ? enabled : true;
};
